import { useEffect, useState } from 'react'
import type { ComboboxOption } from '@/components/ui/combobox'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    useAssignDriver,
    useAssignableDrivers,
} from '@/hooks/use-sales-order-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    orderId: string
}

/**
 * Popup gán tài xế cho đơn — dành cho tài khoản có quyền
 * `sales_orders.assign_driver` (admin, kế toán). Tài xế đang bận vẫn chọn được
 * vì một chuyến giao chở nhiều đơn; badge chỉ cho biết ai đang gánh nặng hơn.
 */
export function AssignDriverDialog({ open, onOpenChange, orderId }: Props) {
    const { data: drivers = [], isLoading } = useAssignableDrivers(open)
    const assignDriver = useAssignDriver()
    const workflow = useWorkflowTracking('delivery_assign', {
        active: open,
        attemptKey: orderId,
        operation: 'assign',
    })
    const [selectedDriverId, setSelectedDriverId] = useState('')

    useEffect(() => {
        if (!open) setSelectedDriverId('')
    }, [open])

    const options: Array<ComboboxOption> = drivers.map((driver) => ({
        value: driver.id,
        label: driver.full_name,
        render: () => (
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
                {/* Username đi kèm vì hai tài khoản của cùng một người có thể
                    trùng cả tên lẫn số điện thoại — không có nó thì người gán
                    đơn không biết mình đang chọn account nào. */}
                <div className="flex min-w-0 flex-col">
                    <span className="truncate">{driver.full_name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                        {driver.username}
                    </span>
                </div>
                <Badge
                    variant={
                        driver.active_order_count === 0
                            ? 'secondary'
                            : 'outline'
                    }
                    className="shrink-0 text-xs"
                >
                    {driver.active_order_count === 0
                        ? m.assign_driver_badge_free()
                        : m.assign_driver_badge_busy({
                              count: driver.active_order_count,
                          })}
                </Badge>
            </div>
        ),
    }))

    const handleSubmit = async () => {
        if (!selectedDriverId) {
            workflow.step('validation_failed')
            return
        }
        try {
            await workflow.run(
                () =>
                    assignDriver.mutateAsync({
                        id: orderId,
                        data: { driver_id: selectedDriverId },
                    }),
                (result) => result.data?.id,
            )
            onOpenChange(false)
        } catch {
            // Toast lỗi do useAssignDriver.onError lo. Giữ popup mở để admin
            // chọn tài xế khác khi đơn vừa bị tài xế khác nhận mất.
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.assign_driver_title()}</DialogTitle>
                    <DialogDescription>
                        {m.assign_driver_desc()}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <label className="text-sm font-medium">
                        {m.assign_driver_label()} *
                    </label>
                    {!isLoading && drivers.length === 0 ? (
                        <p className="text-sm text-destructive">
                            {m.assign_driver_none()}
                        </p>
                    ) : (
                        <Combobox
                            options={options}
                            value={selectedDriverId}
                            onChange={(value) => {
                                workflow.step('selection_changed')
                                setSelectedDriverId(value)
                            }}
                            loading={isLoading}
                            placeholder={m.assign_driver_placeholder()}
                            searchPlaceholder={m.assign_driver_search_placeholder()}
                            emptyText={m.assign_driver_not_found()}
                        />
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        onClick={() => void handleSubmit()}
                        disabled={!selectedDriverId || assignDriver.isPending}
                    >
                        {assignDriver.isPending
                            ? m.common_saving()
                            : m.assign_driver_submit()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
